import React, { useEffect, useState } from "react";
import { FaChevronRight, FaArrowLeft } from "react-icons/fa";
import axios from "../../../api/axios";
import HeaderWithBack from "../../../components/HeaderWithBack";
import { useAuth } from "../../../contexts/AuthContext";
import { useAuthStore } from "../../../stores/authStores";

interface PaymentMethodData {
  id: number;
  name: string;
  code: string;
  image_url?: string;
  displayName?: string;
  displayIcon?: string;
}

interface PaymentCategory {
  id: number;
  name: string;
  image_url?: string;
  method: PaymentMethodData[];
}

interface Props {
  defaultPayment?: PaymentMethodData | null;
  onSelect: (payment: PaymentMethodData) => void;
}

const PaymentMethod: React.FC<Props> = ({ defaultPayment, onSelect }) => {
    const storeToken = useAuthStore((state) => state.token);
    const { token: contextToken } = useAuth();
    const token = storeToken || contextToken;

    const [categories, setCategories] = useState<PaymentCategory[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedCategory, setSelectedCategory] = useState<PaymentCategory | null>(null);
    const [selectedPayment, setSelectedPayment] = useState<PaymentMethodData | null>(null);

    useEffect(() => {
        if (!token) return;

        setLoading(true);
        axios
            .get(`payment?configuration=category_payment_live_consultation`, {
                headers: { Authorization: `Bearer ${token}` },
            })
            .then((res) => {
                const data: PaymentCategory[] = res.data?.data || [];
                setCategories(data.filter((cat) => cat.method?.length > 0));
            })
            .catch((err) => console.log("Payment API Error:", err))
            .finally(() => setLoading(false));
    }, [token]);

    useEffect(() => {
        if (defaultPayment) {
            setSelectedPayment(defaultPayment);
        }
    }, [defaultPayment]);

    const isQris = (cat: PaymentCategory) => cat.name.toLowerCase().includes("qris");

    const handleSelectPayment = (pm: PaymentMethodData, category?: PaymentCategory) => {
        setSelectedPayment(pm);
        onSelect({
            ...pm,
            displayName: pm.displayName || pm.name,
            displayIcon: pm.displayIcon || pm.image_url || category?.image_url,
        });
    };

    const handleSelectCategory = (cat: PaymentCategory) => {
        if (isQris(cat) && cat.method.length > 0) {
            handleSelectPayment(
                { ...cat.method[0], displayName: cat.name, displayIcon: cat.image_url },
                cat
            );
            return;
        }

        setSelectedCategory(cat);
    };

    return (
        <div className="min-h-screen bg-gray-100">
        <HeaderWithBack title="Metode Pembayaran" />

        {!selectedCategory && (
            <div className="px-4 mt-4 space-y-3">
            {loading && <p className="text-sm text-gray-500 text-center">Memuat metode pembayaran...</p>}
            {!loading && categories.length === 0 && (
                <p className="text-sm text-gray-500 text-center">Metode pembayaran belum tersedia.</p>
            )}
            {categories.map((cat) => (
                <div
                    key={cat.id}
                    onClick={() => handleSelectCategory(cat)}
                    className="bg-white rounded-xl shadow p-4 flex justify-between items-center cursor-pointer"
                >
                <div className="flex items-center gap-3">
                    {cat.image_url && (
                        <img
                            src={cat.image_url}
                            alt={cat.name}
                            className="w-8 h-8 object-contain"
                        />
                    )}
                    <span className="font-medium text-gray-800">
                        {cat.name}
                    </span>
                </div>
                {isQris(cat) ? (
                    <input
                        type="radio"
                        checked={cat.method.some((pm) => pm.id === selectedPayment?.id)}
                        readOnly
                        className="w-5 h-5 accent-teal-500"
                    />
                    ) : (
                    <FaChevronRight className="text-gray-400" />
                )}
                </div>
            ))}
            </div>
        )}

        {selectedCategory && (
            <div className="px-4 mt-4 space-y-3">
            <button
                onClick={() => setSelectedCategory(null)}
                className="flex items-center gap-2 text-sm text-gray-600"
            >
                <FaArrowLeft /> Kembali
            </button>

            {selectedCategory.method.map((pm) => (
                <div
                    key={pm.id}
                    onClick={() => handleSelectPayment(pm, selectedCategory)}
                    className={`bg-white rounded-xl shadow p-4 flex justify-between items-center cursor-pointer
                    ${selectedPayment?.id === pm.id ? "border-2 border-teal-500" : ""}
                    `}
                >
                    <div className="flex items-center gap-3">
                        <img
                            src={pm.image_url}
                            alt={pm.name}
                            className="w-10 h-10 object-contain"
                        />
                        <p className="font-medium text-gray-800">{pm.name}</p>
                    </div>
                    <input
                        type="radio"
                        checked={selectedPayment?.id === pm.id}
                        onChange={() => handleSelectPayment(pm, selectedCategory)}
                        className="w-5 h-5 accent-teal-500"
                    />
                </div>
            ))}
            </div>
        )}
    </div>
  );
};

export default PaymentMethod;
